package co.maytes.standupbot.dashboard.features.releases

import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.integration.GithubReposResponseDto
import co.maytes.standupbot.shared.dto.ship.ReleaseSettingsDto
import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import io.ktor.http.encodeURLQueryComponent
import kotlinx.coroutines.CoroutineScope

class ReleasesContainer(
    scope: CoroutineScope,
    private val api: ApiClient,
) : Container<ReleasesContract.State, ReleasesContract.Intent, ReleasesContract.Action> {
    override val store =
        scope.store<
            ReleasesContract.State,
            ReleasesContract.Intent,
            ReleasesContract.Action,
        >(initialState = ReleasesContract.State()) {
            configure(name = "Releases")
            logging()
            reduce { intent ->
                when (intent) {
                    is ReleasesContract.Intent.Load -> {
                        updateState { copy(workspaceId = intent.workspaceId, isAdmin = intent.isAdmin, repo = intent.repo ?: repo) }
                        if (intent.isAdmin) {
                            loadSettings(intent.workspaceId)
                            loadGithubReach(intent.workspaceId)
                        }
                        loadFirstPage(intent.workspaceId)
                    }
                    ReleasesContract.Intent.LoadMore -> loadMore()
                    is ReleasesContract.Intent.SetRepo -> {
                        updateState { copy(repo = intent.repo) }
                        currentState.workspaceId?.let { loadFirstPage(it) }
                    }
                    ReleasesContract.Intent.GoToSettings -> action(ReleasesContract.Action.Navigate("/app/settings/releases"))
                    ReleasesContract.Intent.GoToIntegrations -> action(ReleasesContract.Action.Navigate("/app/settings/integrations"))
                }
            }
        }

    private suspend fun StoreScope<ReleasesContract.State, ReleasesContract.Action>.loadSettings(workspaceId: String) {
        when (val result = api.get<ReleaseSettingsDto>("/api/v1/workspaces/$workspaceId/releases/settings")) {
            is Result.Ok -> updateState { copy(settings = result.value) }
            is Result.Err -> Unit
        }
    }

    private suspend fun StoreScope<ReleasesContract.State, ReleasesContract.Action>.loadGithubReach(workspaceId: String) {
        val reach =
            when (val result = api.get<GithubReposResponseDto>("/api/v1/workspaces/$workspaceId/integrations/github/repos")) {
                is Result.Ok -> githubReach(status = null, repoCount = result.value.repos.size)
                is Result.Err -> githubReach(status = (result.error as? ApiError.Http)?.status, repoCount = null)
            }
        updateState { copy(github = reach) }
    }

    private suspend fun StoreScope<ReleasesContract.State, ReleasesContract.Action>.loadFirstPage(workspaceId: String) {
        updateState {
            copy(
                status = if (showsRows) ReleasesContract.Status.Refreshing else ReleasesContract.Status.Loading,
                hasEntered = showsRows,
            )
        }
        when (val result = fetch(workspaceId, currentState.repo, cursor = null)) {
            is Result.Ok -> updateState { withFirstPage(result.value) }
            is Result.Err -> updateState { copy(status = ReleasesContract.Status.Error(result.error)) }
        }
    }

    private suspend fun StoreScope<ReleasesContract.State, ReleasesContract.Action>.loadMore() {
        val state = currentState
        val workspaceId = state.workspaceId ?: return
        val cursor = state.pageInfo?.nextCursor ?: return
        if (state.status == ReleasesContract.Status.LoadingMore) return
        updateState { copy(status = ReleasesContract.Status.LoadingMore, hasEntered = true, loadMoreError = null) }
        when (val result = fetch(workspaceId, state.repo, cursor)) {
            is Result.Ok -> updateState { withNextPage(result.value) }
            is Result.Err -> updateState { withLoadMoreFailure(result.error) }
        }
    }

    private suspend fun fetch(
        workspaceId: String,
        repo: String?,
        cursor: String?,
    ): Result<PagedResponse<ShipEventDto>, ApiError> {
        val query =
            listOfNotNull(
                "limit=$PAGE_SIZE",
                repo?.let { "repo=${it.encodeURLQueryComponent()}" },
                cursor?.let { "cursor=${it.encodeURLQueryComponent()}" },
            ).joinToString("&")
        return api.get<PagedResponse<ShipEventDto>>("/api/v1/workspaces/$workspaceId/releases?$query")
    }

    private companion object {
        const val PAGE_SIZE = 50
    }
}
